import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { UploadCloud, FileText, CheckCircle2 } from "lucide-react";

const TEMPLATE = "name,category,criticality,environment,owner,ip_address,status,description";
const DEFAULT_MAX_MB = 50;

export default function CsvImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [maxMb, setMaxMb] = useState(DEFAULT_MAX_MB);

  // La taille max fait AUTORITÉ côté backend ; le frontend la lit pour une prévention UX.
  useEffect(() => {
    if (!open) return;
    let active = true;
    api.get("/config/upload")
      .then(({ data }) => { if (active && data?.max_upload_mb) setMaxMb(data.max_upload_mb); })
      .catch(() => {});
    return () => { active = false; };
  }, [open]);

  const reset = () => { setFile(null); setResult(null); setBusy(false); };

  const selectFile = (f) => {
    if (!f) { setFile(null); return; }
    if (f.size > maxMb * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (${(f.size / (1024 * 1024)).toFixed(1)} Mo). Maximum : ${maxMb} Mo.`);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Fichier trop volumineux. Maximum : ${maxMb} Mo.`);
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/assets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created} actif(s) importé(s)`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Import CSV — Actifs</DialogTitle>
          <DialogDescription>Chargez un fichier .csv d'actifs (max <span data-testid="csv-max-size">{maxMb}</span> Mo, 100 000 lignes).</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-2">
            <div className="text-xs text-muted-foreground">
              En-têtes attendus :
              <code className="block mt-1 p-2 bg-secondary rounded-sm font-mono text-[11px] break-all">{TEMPLATE}</code>
            </div>
            <label className="flex flex-col items-center justify-center border border-dashed border-border rounded-sm py-10 cursor-pointer hover:bg-secondary/40 transition-colors">
              <UploadCloud className="h-8 w-8 text-muted-foreground mb-3" />
              <span className="text-sm">{file ? file.name : "Cliquez pour choisir un fichier .csv"}</span>
              <input type="file" accept=".csv" className="hidden"
                onChange={(e) => selectFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
            </label>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="csv-import-result">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">{result.created} actif(s) importé(s)</span>
            </div>
            {result.error_count > 0 && (
              <div className="text-sm text-orange-400">
                {result.error_count} ligne(s) en erreur :
                <ul className="mt-1 space-y-1 max-h-32 overflow-y-auto cros-scroll">
                  {result.errors.map((er, i) => (
                    <li key={i} className="font-mono text-xs">Ligne {er.line}: {er.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button className="rounded-sm gap-2" onClick={submit} disabled={!file || busy} data-testid="csv-submit-button">
                <FileText className="h-4 w-4" /> {busy ? "Import…" : "Importer"}
              </Button>
            </>
          ) : (
            <Button className="rounded-sm" onClick={() => onOpenChange(false)}>Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
